import { Badge, Card, EmptyState } from '@hgc/ui';
import type { JSX } from 'react';

import { useLinkStats, useLinkStatus, useNotices, useNow, usePrimaryVehicle, useVehicleState } from '../../runtime/hooks';
import { severityLevel } from '../../runtime/notices';
import { MetricTable } from './MetricTable';
import { buildLinkStatRows, buildTelemetryRows } from './rows';
import { clockText } from './format';

/**
 * 实时遥测读数。**这是本阶段唯一能被验证的东西**：如果这里的数字在动，
 * 说明"传输 → 解析 → 会话 → 车辆状态 → React"这整条链路是通的。
 *
 * 它刻意不是最终界面（姿态仪与飞行仪表属于 fly-hud）：现在需要的是一个**可读的证据**，
 * 每行都写着数值来自哪条消息，因此当某个值是 `—` 时能立刻判断出是"机体没发"还是"没解析到"。
 */
export function TelemetryCard(): JSX.Element {
  const status = useLinkStatus();
  const vehicle = usePrimaryVehicle();
  const state = useVehicleState();
  const now = useNow(1000);

  if (status.phase !== 'connected') {
    return (
      <Card title="机体遥测">
        <EmptyState
          level="warning"
          title="链路未连接，没有实时遥测数据"
          description={
            '断开后不再显示上一帧的读数：它只会越来越旧，而一个不再更新的数字比没有数字更危险。' +
            (status.reason === null ? '' : `当前状态：${status.reason}`)
          }
        />
      </Card>
    );
  }

  if (vehicle === null || state === null) {
    return (
      <Card title="机体遥测">
        <EmptyState
          level="info"
          title="链路已连接，但还没有收到任何机体的帧"
          description="链路上的第一帧到达时，这里会自动出现这辆车的读数（不需要手动刷新）。"
        />
      </Card>
    );
  }

  return (
    <Card
      title="机体遥测"
      subtitle={`${vehicle.label}${vehicle.isAutopilot === true ? ' · 飞控' : ''}`}
      actions={
        <Badge level="ok" size="sm" dot>
          实时
        </Badge>
      }
      padded={false}
    >
      <MetricTable rows={buildTelemetryRows(state, now)} label="机体遥测读数" />
    </Card>
  );
}

/**
 * 链路统计。
 *
 * 与遥测分开：遥测回答"机体现在怎么样"，统计回答"这条线好不好"。混在一张表里，
 * "CRC 错误开始涨了"会被淹没在姿态数值中间。
 */
export function LinkStatsCard(): JSX.Element {
  const stats = useLinkStats();
  const status = useLinkStatus();

  if (stats === null) {
    return (
      <Card title="链路统计">
        <EmptyState
          compact
          title="还没有链路"
          description="连接之后这里会显示收发帧数、速率与各类错误计数；它们来自会话层的解析器统计。"
        />
      </Card>
    );
  }

  return (
    <Card
      title="链路统计"
      subtitle={status.transportLabel ?? undefined}
      actions={
        <Badge size="sm" level="neutral" variant="outline">
          {status.linkState}
        </Badge>
      }
      padded={false}
    >
      <MetricTable rows={buildLinkStatRows(stats)} label="链路统计" />
    </Card>
  );
}

/**
 * 机体播报的状态文本。
 *
 * 这是 `@hgc/core` 的 alerts 管理器接入**之前**唯一真实的告警来源：展示的是机体真的发过来的
 * 东西，而不是本地规则推断出来的结论。等告警总线接进来，这张卡片的调用方一行都不用改
 * （`useNotices` 的实现会换掉）。
 */
export function NoticesCard(): JSX.Element {
  const notices = useNotices();

  return (
    <Card
      title="最近状态文本"
      subtitle="机体自己播报的 STATUSTEXT（新 → 旧，最多 8 条）"
    >
      {notices.length === 0 ? (
        <EmptyState
          compact
          title="机体还没有播报过状态文本"
          description="PreArm 失败、EKF 异常、模式切换都会走这条通道；它在解锁与起飞前尤其重要。"
        />
      ) : (
        <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
          {notices.map((notice, index) => (
            // 同一毫秒可能有多条分片拼接的状态文本，所以键里带上序号
            <li key={`${notice.at}-${index}`} className="flex items-start gap-2">
              <Badge size="sm" variant="outline" level={severityLevel(notice.severity)}>
                {notice.severityName?.replace('MAV_SEVERITY_', '') ?? notice.severity}
              </Badge>
              <span className="min-w-0 flex-1 text-xs text-fg">{notice.text}</span>
              <span className="shrink-0 font-mono text-[10px] text-fg-subtle">
                {clockText(notice.at)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
